import React, { useContext, useState } from 'react';
import { makeStyles } from '@mui/styles';
import { Box, Menu, MenuItem, useTheme } from '@mui/material';
import CssBaseline from '@mui/material/CssBaseline';
import NotificationSideBar from '../Settings/Notifications/PushNotifications/NotificationSideBar';
import { ColorModeContext } from '../store';
import { useTranslation } from 'react-i18next';
import useLogout from '../CustomHooks/useLogout';
import { appInsights } from '../auth/appInsight';
import { images, topNavbar } from '../url/SvgLinks';
import { getDynamicBorder, getStringProduct } from '../utils/utilityFunctions';

const avatarimage = images.avatarimage;
const freedomIcon = topNavbar.freedomIcon;
const Sun = topNavbar.Sun;
const darkMode = topNavbar.darkMode;

const useStyle = makeStyles((theme) => ({
  topNavBar: {
    zIndex: 5,
    width: '100%',
    maxWidth: '90vw',
    height: '50px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    position: 'fixed',
    borderRadius: '0px 0px 20px 20px',
    borderTop: 'none',
    border: `2px solid ${theme.palette.border.border}`,
    background: `${theme.palette.topbar.barBgColor}`,
    padding: '0 3%',
    top: '0', // Make sure the navbar is fixed at the top
    left: '50%',
    transform: 'translateX(-50%)', // Only translate horizontally to center
    [theme.breakpoints.down('sm')]: {
      maxWidth: '100vw'
    },
    [theme.breakpoints.up('xll')]: {
      height: getStringProduct(1.25, '50px'),
      borderRadius: getStringProduct(1.25, '0px 0px 20px 20px'),
      border: getDynamicBorder(1.25, `2px solid ${theme.palette.border.border}`),
      borderTop: 'none'
    },
    [theme.breakpoints.up('xlll')]: {
      height: getStringProduct(1.66, '50px'),
      borderRadius: getStringProduct(1.66, '0px 0px 20px 20px'),
      border: getDynamicBorder(1.66, `2px solid ${theme.palette.border.border}`),
      borderTop: 'none'
    },
    [theme.breakpoints.up('xl4')]: {
      height: getStringProduct(2.46, '50px'),
      borderRadius: getStringProduct(2.46, '0px 0px 20px 20px'),
      border: getDynamicBorder(2.46, `2px solid ${theme.palette.border.border}`),
      borderTop: 'none'
    }
  },
  freedomTextIcon: {
    Width: '124.94px',
    Height: '29.74px',
    [theme.breakpoints.up('xll')]: {
      Width: getStringProduct(1.25, '124.94px'),
      Height: getStringProduct(1.25, '29.74px')
    },
    [theme.breakpoints.up('xlll')]: {
      Width: getStringProduct(1.66, '124.94px'),
      Height: getStringProduct(1.66, '29.74px')
    },
    [theme.breakpoints.up('xl4')]: {
      Width: getStringProduct(2.46, '124.94px'),
      Height: getStringProduct(2.46, '29.74px')
    }
  },
  menuIcondiv: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: 32,
    height: 32,
    border: `2px solid ${theme.palette.icon.borderColor}`,
    // backgroundColor: `${theme.palette.icon.iconBgColor}`,
    '&:hover': {
      // backgroundColor: `${theme.palette.primary.tabHover}`,
    },
    borderRadius: '10px',
    margin: '10px',
    cursor: 'pointer',
    [theme.breakpoints.up('xll')]: {
      width: getStringProduct(1.25, 32),
      height: getStringProduct(1.25, 32),
      border: getDynamicBorder(1.25, `2px solid ${theme.palette.icon.borderColor}`),
      borderRadius: getStringProduct(1.25, '10px'),
      margin: getStringProduct(1.25, '10px')
    },
    [theme.breakpoints.up('xlll')]: {
      width: getStringProduct(1.66, 32),
      height: getStringProduct(1.66, 32),
      border: getDynamicBorder(1.66, `2px solid ${theme.palette.icon.borderColor}`),
      borderRadius: getStringProduct(1.66, '10px'),
      margin: getStringProduct(1.66, '10px')
    },
    [theme.breakpoints.up('xl4')]: {
      width: getStringProduct(2.46, 32),
      height: getStringProduct(2.46, 32),
      border: getDynamicBorder(2.46, `2px solid ${theme.palette.icon.borderColor}`),
      borderRadius: getStringProduct(2.46, '10px'),
      margin: getStringProduct(2.46, '10px')
    }
  },

  menuProfilediv: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: 32,
    height: 32,
    borderRadius: '10px',
    margin: '10px',
    cursor: 'pointer',
    [theme.breakpoints.up('xll')]: {
      width: getStringProduct(1.25, 32),
      height: getStringProduct(1.25, 32),
      borderRadius: getStringProduct(1.25, '10px'),
      margin: getStringProduct(1.25, '10px')
    },
    [theme.breakpoints.up('xlll')]: {
      width: getStringProduct(1.66, 32),
      height: getStringProduct(1.66, 32),
      borderRadius: getStringProduct(1.66, '10px'),
      margin: getStringProduct(1.66, '10px')
    },
    [theme.breakpoints.up('xl4')]: {
      width: getStringProduct(2.46, 32),
      height: getStringProduct(2.46, 32),
      borderRadius: getStringProduct(2.46, '10px'),
      margin: getStringProduct(2.46, '10px')
    }
  },
  menuIcon: {
    Width: '17.88px',
    Height: '19px',
    background: `${theme.palette.icon.iconColor}`,
    '&:hover': {
      background: `${theme.palette.icon.iconHoverColor}`
    },
    [theme.breakpoints.up('xll')]: {
      Width: getStringProduct(1.25, '17.88px'),
      Height: getStringProduct(1.25, '19px')
    },
    [theme.breakpoints.up('xlll')]: {
      Width: getStringProduct(1.66, '17.88px'),
      Height: getStringProduct(1.66, '19px')
    },
    [theme.breakpoints.up('xl4')]: {
      Width: getStringProduct(2.46, '17.88px'),
      Height: getStringProduct(2.46, '19px')
    }
  }
}));

const TopNavBar1 = () => {
  const classes = useStyle();
  const theme = useTheme();
  const {
    colorMode: { toggleMode, mode }
  } = useContext(ColorModeContext);
  const { t } = useTranslation();

  const [anchorEl, setAnchorEl] = useState(null);
  const [notificationAnchorEl, setNotificationAnchorEl] = useState(null);

  const isNotifiactionMenuBarOpen = Boolean(notificationAnchorEl);
  const isMenuOpen = Boolean(anchorEl);

  //Selector

  const handleMenuClose = () => {
    setAnchorEl(null);
    // handleMobileMenuClose();
  };
  const handleNotificationbarClose = () => {
    setNotificationAnchorEl(null);
  };
  const { handleLogOutRedirect } = useLogout();

  const handleProfileMenuOpen = (event) => {
    // devConsoleLog("image ", event)
    setAnchorEl(event.currentTarget);
  };

  const notificationMenuId = 'primary-search-account-men';
  const renderNotificationMenu = (
    <Menu
      anchorEl={notificationAnchorEl}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right'
      }}
      id={notificationMenuId}
      keepMounted
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right'
      }}
      open={isNotifiactionMenuBarOpen}
      onClose={handleNotificationbarClose}
    >
      <NotificationSideBar handleNotificationbarClose={handleNotificationbarClose} />
    </Menu>
  );

  const menuId = 'primary-search-account-menu';
  const renderMenu = (
    <Menu
      anchorEl={anchorEl}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right'
      }}
      id={menuId}
      keepMounted
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right'
      }}
      open={isMenuOpen}
      onClose={handleMenuClose}
      className=".mui-fixed"
    >
      <MenuItem
        sx={{
          [theme.breakpoints.up('xll')]: {
            fontSize: getStringProduct(1.25, '16px'),
            padding: getStringProduct(1.25, '8px')
          },
          [theme.breakpoints.up('xlll')]: {
            fontSize: getStringProduct(1.66, '16px'),
            padding: getStringProduct(1.66, '8px')
          },
          [theme.breakpoints.up('xl4')]: {
            fontSize: getStringProduct(2.46, '16px'),
            padding: getStringProduct(2.46, '8px')
          }
        }}
        //  sx={{borderBottom:"1px solid #A9A9A9"}}
        onClick={handleLogOutRedirect}
      >
        {t('Logout')}
      </MenuItem>
    </Menu>
  );
  try {
    return (
      <React.Fragment>
        <CssBaseline />
        <Box className={classes.topNavBar}>
          <Box className={classes.freedomTextIcon}>
            <Box
              component={'img'}
              src={freedomIcon}
              sx={{
                height: 30,
                width: 125,
                [theme.breakpoints.up('xll')]: { height: getStringProduct(1.25, 30), width: getStringProduct(1.25, 125) },
                [theme.breakpoints.up('xlll')]: { height: getStringProduct(1.66, 30), width: getStringProduct(1.66, 125) },
                [theme.breakpoints.up('xl4')]: { height: getStringProduct(2.46, 30), width: getStringProduct(2.46, 125) },
              }}
              alt="Freedom"
            />
          </Box>

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'center',
              alignItems: 'center'
            }}
          >
            {/* <Box
              className={classes.menuIcondiv}
              // onClick={toggleMode}
              onClick={toggleMode}
            >
              {mode === 'dark' ? (
                // <FreedomFollowUpIcon iconimg={darkMode} borderradius="5px" />
                <img
                  src={Sun}
                  style={{
                    height: 32,
                    width: 32,
                    border: `2px solid ${theme.palette.border.border}`,
                    padding: '5px',
                    borderRadius: '10px'
                  }}
                  alt="Mode"
                />
              ) : (
                <img
                  src={darkMode}
                  style={{
                    height: 32,
                    width: 32,
                    border: `2px solid ${theme.palette.border.border}`,
                    padding: '5px',
                    borderRadius: '10px'
                  }}
                  alt="Mode"
                />
              )}
            </Box> */}

            <Box className={classes.menuProfilediv} onClick={handleProfileMenuOpen}>
              <Box
                component={'img'}
                src={avatarimage}
                sx={{
                  borderRadius: '8px',
                  width: '100%',
                  height: '100%',
                  border: `2px solid ${theme.palette.border.border}`,
                  [theme.breakpoints.up('xll')]: {
                    borderRadius: getStringProduct(1.25, '8px'),
                    border: getDynamicBorder(1.25, `2px solid ${theme.palette.border.border}`)
                  },
                  [theme.breakpoints.up('xlll')]: {
                    borderRadius: getStringProduct(1.66, '8px'),
                    border: getDynamicBorder(1.66, `2px solid ${theme.palette.border.border}`)
                  },
                  [theme.breakpoints.up('xl4')]: {
                    borderRadius: getStringProduct(2.46, '8px'),
                    border: getDynamicBorder(2.46, `2px solid ${theme.palette.border.border}`)
                  }
                }}
                alt="profile"
              />
            </Box>
          </Box>
        </Box>
        {renderMenu}
        {renderNotificationMenu}
      </React.Fragment>
    );
  } catch (error) {
    appInsights.trackException({ error: new Error(error) });
  }
};

export default TopNavBar1;
